<script setup lang="ts">
import store from "@/store";
import { computed, ref, watchEffect } from "vue";
import { debounce } from "lodash-es";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

import { toast } from "@/common/utils/toast";
import { base64ToFile } from "@/common/utils/files";

import { companyGet, companyUpdate } from "@/common/api/rest/company";
import {
  addressList,
  addressCreate,
  addressUpdate,
  addressDelete,
} from "@/common/api/rest/address";
import {
  contactList,
  contactCreate,
  contactUpdate,
  contactDelete,
} from "@/common/api/rest/contact";
import {
  fileAvatarCreate,
  fileAvatarUpdate,
  fileAvatarDelete,
} from "@/common/api/rest/file/avatar";
import {
  employeeCreate,
  employeeList,
  employeUpload,
  employeModelCSV,
  employeeShow,
  employeeUpdate,
  employeeDelete,
} from "@/common/api/rest/employee";

import { CompanyType, type CompanyPayloadType } from "@/common/types/company";
import type { AddressType, ContactType, FileType } from "@/common/types/common";
import type {
  EmployeePayloadType,
  EmployeeListType,
} from "@/common/types/employee";

import { RegisterTheme } from "@/common/themes";

import {
  svgIcon,
  iconMenu,
  baseTable,
  baseConfirm,
  baseModal,
} from "@/common/components";

import {
  fileUploadForm,
  companyForm,
  addressForm,
  contactForm,
  employeeForm,
} from "@/components";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const addressData = ref<AddressType[]>([]);
const contactData = ref<AddressType[]>([]);
const employeeData = ref<EmployeeListType[]>([]);

const selectedMenu = ref<string>("home");
const companyData = ref<CompanyType | null>(null);
const companyPayload = ref<CompanyPayloadType | null>(null);
const addressPayload = ref<AddressType | null>(null);
const contactPayload = ref<ContactType | null>(null);
const employeePayload = ref<EmployeePayloadType | null>(null);

const selectedAddress = ref<AddressType | null>(null);
const selectedContact = ref<ContactType | null>(null);
const selectedEmployee = ref<EmployeeListType | null>(null);

const fileEmployee = ref<FileType | null>(null);
const fileExtensions = ref(["csv"]);

const addressEdit = ref(false);
const showModalAddress = ref(false);
const showDelAddress = ref(false);

const contactEdit = ref(false);
const showModalContact = ref(false);
const showDelContact = ref(false);

const employeeEdit = ref(false);
const showModalFileEmployee = ref(false);
const showModalEmployee = ref(false);
const showDelEmployee = ref(false);

const showDelAvatar = ref(false);

const addressLimit = ref<number>(5);
const addressPage = ref<number>(0);
const addressPages = ref<number>(0);
const addressSearch = ref<string>("");
const addressSearched = ref<number>(0);
const addressTotal = ref<number>(0);

const contactLimit = ref<number>(5);
const contactPage = ref<number>(0);
const contactPages = ref<number>(0);
const contactSearch = ref<string>("");
const contactSearched = ref<number>(0);
const contactTotal = ref<number>(0);

const employeeLimit = ref<number>(5);
const employeePage = ref<number>(0);
const employeePages = ref<number>(0);
const employeeSearch = ref<string>("");
const employeeSearched = ref<number>(0);
const employeeTotal = ref<number>(0);

const accountId = computed(() => String(route.params.id));
const companyId = computed(() => String(route.params.company));

const addressHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "address", label: t("common.labels.address") },
  { key: "cep", label: t("common.labels.cep") },
]);

const contactHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "type", label: t("common.labels.type") },
  { key: "contact", label: t("common.labels.contact") },
]);

const employeeHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "role", label: t("common.labels.role") },
  { key: "sector", label: t("common.labels.sector") },
  { key: "status", label: t("common.labels.status") },
]);

const modalAddressTitle = computed(() =>
  addressEdit.value
    ? t("common.labels.edit", {
        concat: t("common.labels.address"),
      }).toUpperCase()
    : t("common.labels.add", {
        concat: t("common.labels.address"),
      }).toUpperCase(),
);

const modalContactTitle = computed(() =>
  addressEdit.value
    ? t("common.labels.edit", {
        concat: t("common.labels.contact"),
      }).toUpperCase()
    : t("common.labels.add", {
        concat: t("common.labels.contact"),
      }).toUpperCase(),
);

const modalEmployeeTitle = computed(() =>
  employeeEdit.value
    ? t("common.labels.edit", {
        concat: t("common.labels.employee"),
      }).toUpperCase()
    : t("common.labels.add", {
        concat: t("common.labels.employee"),
      }).toUpperCase(),
);

const menuTabs = computed(() => [
  {
    key: "home",
    icon: "home-line",
    label: t("common.labels.company"),
  },
  {
    key: "address",
    icon: "pin",
    label: t("common.labels.address"),
  },
  {
    key: "contact",
    icon: "phone",
    label: t("common.labels.contacts"),
  },
  {
    key: "employee",
    icon: "card",
    label: t("common.labels.employees"),
  },
]);

function back() {
  router.push(`/${accountId.value}/register/company`);
}

function fetchCompany() {
  companyGet(accountId.value, companyId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        companyData.value = data;

        companyPayload.value = {
          name: data.name,
          fantasy_name: data.fantasy_name,
          description: data.description,
          document_number: data.document_number,
          document_name: data.document_name,
          document_type: data.document_type,
          cnae: data.cnae,
          danger: data.danger?.toString(),
          number_of_employee: data.number_of_employee,
        };

        addressData.value = data.address;
        contactData.value = data.contacts;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateCompany(data: CompanyPayloadType) {
  companyUpdate(accountId.value, companyId.value, data)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchCompany();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// ADDRESS

function fetchAddress() {
  addressList(accountId.value, companyId.value, {
    page: addressPage.value,
    limit: addressLimit.value,
    search: addressSearch.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        addressData.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addAddress() {
  addressEdit.value = false;
  showModalAddress.value = true;
}

function addressCancel() {
  addressEdit.value = false;
  showModalAddress.value = false;
  addressPayload.value = null;
}

function addAddressConfirm() {
  if (!addressPayload.value) return;

  showModalAddress.value = false;
  store.commit("SET_LOADING", true);

  addressCreate(accountId.value, companyId.value, addressPayload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchAddress();
        addressCancel();
        toast({ message: message, type: "success" });
      } else {
        showModalAddress.value = true;
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      showModalAddress.value = true;
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updateAddress(data: AddressType) {
  selectedAddress.value = data;
  addressPayload.value = data;
  showModalAddress.value = true;
  addressEdit.value = true;
}

function updateAddressConfirm() {
  if (!selectedAddress.value) return;

  showModalAddress.value = false;
  store.commit("SET_LOADING", true);

  addressUpdate(
    accountId.value,
    selectedAddress.value.address_id as string,
    addressPayload.value as AddressType,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchAddress();
        addressCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function submitAddress() {
  if (!addressEdit.value) {
    addAddressConfirm();
  } else {
    updateAddressConfirm();
  }
}

function removeAddress(data: AddressType) {
  selectedAddress.value = data;
  showDelAddress.value = true;
}

function removeAddressCancel() {
  selectedAddress.value = null;
  showDelAddress.value = false;
}

function removeAddressConfirm() {
  if (!selectedAddress.value) return;

  showModalAddress.value = false;
  store.commit("SET_LOADING", true);

  addressDelete(accountId.value, selectedAddress.value.address_id as string)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchAddress();
        removeAddressCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

// CONTACT

function fetchContacts() {
  contactList(accountId.value, companyId.value, {
    page: contactPage.value,
    limit: contactLimit.value,
    search: contactSearch.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        contactData.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addContact() {
  showModalContact.value = true;
}

function contactCancel() {
  contactEdit.value = false;
  showModalContact.value = false;
  contactPayload.value = null;
}

function addContactConfirm() {
  if (!contactPayload.value) return;

  showModalContact.value = false;
  store.commit("SET_LOADING", true);

  contactCreate(accountId.value, companyId.value, contactPayload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchContacts();
        contactCancel();
        toast({ message: message, type: "success" });
      } else {
        showModalContact.value = true;
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      showModalContact.value = true;
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updatedContact(data: ContactType) {
  contactEdit.value = true;
  contactPayload.value = data;
  selectedContact.value = data;
  showModalContact.value = true;
}

function updatedContactConfirm() {
  if (!contactPayload.value && !selectedContact.value) return;

  showModalContact.value = false;
  store.commit("SET_LOADING", true);

  contactUpdate(
    accountId.value,
    selectedContact.value?.contact_id as string,
    contactPayload.value as ContactType,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchContacts();
        contactCancel();
        toast({ message: message, type: "success" });
      } else {
        showModalContact.value = true;
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      showModalContact.value = true;
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function submitContact() {
  if (!contactEdit.value) {
    addContactConfirm();
  } else {
    updatedContactConfirm();
  }
}

function removeContact(data: ContactType) {
  selectedContact.value = data;
  showDelContact.value = true;
}

function removeContactCancel() {
  selectedContact.value = null;
  showDelContact.value = false;
}

function removeContactConfirm() {
  if (!selectedContact.value) return;

  showDelContact.value = false;
  store.commit("SET_LOADING", true);

  contactDelete(accountId.value, selectedContact.value?.contact_id as string)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchContacts();
        removeContactCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

// EMPLOYESS

function fecthEmployees() {
  employeeList(accountId.value, companyId.value, {
    page: employeePage.value,
    search: employeeSearch.value,
    limit: employeeLimit.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        employeeData.value = data.list;
        employeePage.value = data.infos.page;
        employeePages.value = data.infos.pages;
        employeeTotal.value = data.infos.total;
        employeeSearched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addEmployee() {
  showModalEmployee.value = true;
}

function addEmployeeCancel() {
  showModalEmployee.value = false;
  selectedEmployee.value = null;
  employeePayload.value = null;
}

function addEmployeeConfirm() {
  if (!employeePayload.value) return;

  showModalEmployee.value = false;
  store.commit("SET_LOADING", true);

  employeeCreate(accountId.value, companyId.value, employeePayload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fecthEmployees();
        addEmployeeCancel();
        toast({ message: message, type: "success" });
      } else {
        showModalEmployee.value = true;
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      showModalEmployee.value = true;
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editEmployee(employee: EmployeeListType) {
  selectedEmployee.value = employee;

  employeeShow(accountId.value, employee.employee_id)
    .then((response) => {
      const { success, data, message } = response;
      if (success) {
        showModalEmployee.value = true;
        employeeEdit.value = true;
        employeePayload.value = {
          ...data,
        };
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function editEmployeeConfirm() {
  if (!selectedEmployee.value || !employeePayload.value) return;
  showModalEmployee.value = false;
  store.commit("SET_LOADING", true);

  employeeUpdate(
    accountId.value,
    selectedEmployee.value.employee_id,
    employeePayload.value,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fecthEmployees();
        addEmployeeCancel();
        toast({ message: message, type: "success" });
      } else {
        showModalEmployee.value = true;
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      showModalEmployee.value = true;
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function removeEmployee(value: EmployeeListType) {
  showDelEmployee.value = true;
  selectedEmployee.value = value;
}

function removeEmployeeCancel() {
  showDelEmployee.value = false;
  selectedEmployee.value = null;
}

function removeEmployeeConfirm() {
  if (!selectedEmployee.value) return;

  showModalEmployee.value = false;
  store.commit("SET_LOADING", true);

  employeeDelete(accountId.value, selectedEmployee.value.employee_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fecthEmployees();
        removeEmployeeCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function submitEmployee() {
  if (employeeEdit.value) {
    editEmployeeConfirm();
  } else {
    addEmployeeConfirm();
  }
}

function searchEmployee(value: string) {
  employeeSearch.value = value;
  fecthEmployees();
}

function changePageEmployee(value: number) {
  employeePage.value = value;
  fecthEmployees();
}

function uploadEmployee() {
  showModalFileEmployee.value = true;
}

function uploadEmployeeCancel() {
  showModalFileEmployee.value = false;
  fileEmployee.value = null;
}

function uploadEmployeeConfirm() {
  if (!fileEmployee.value) return;

  store.commit("SET_LOADING", true);

  employeUpload(accountId.value, companyId.value, fileEmployee.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fecthEmployees();
        uploadEmployeeCancel();
        toast({ message: message, type: "success" });
      } else {
        showModalFileEmployee.value = true;
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function downloadEmployeeModel() {
  employeModelCSV(accountId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        base64ToFile(data.file, data.name, data.type);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addAvatar(file: string) {
  fileAvatarCreate(accountId.value, {
    owner_id: companyId.value,
    type: "COMPANY",
    file,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchCompany();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateAvatar(file: string) {
  if (!companyData.value?.avatar) return;

  fileAvatarUpdate(accountId.value, companyData.value.avatar.avatar_id, {
    file,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchCompany();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeAvatar(avatar: string) {
  if (companyData.value?.avatar) {
    updateAvatar(avatar);
  } else {
    addAvatar(avatar);
  }
}

function delAvatar() {
  showDelAvatar.value = true;
}

function delAvatarConfirm() {
  if (!companyData.value?.avatar) return;

  fileAvatarDelete(accountId.value, companyData.value!.avatar.avatar_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchCompany();
        showDelAvatar.value = false;
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

const debounceForm = debounce(updateCompany, 1000);

watchEffect(() => {
  console.log("selectedMenu", selectedMenu.value);

  if (companyId.value) {
    if (selectedMenu.value === "home") {
      fetchCompany();
    } else if (selectedMenu.value === "address") {
      fetchAddress();
    } else if (selectedMenu.value === "contact") {
      fetchContacts();
    } else if (selectedMenu.value === "employee") {
      fecthEmployees();
    }
  }
});
</script>

<template>
  <register-theme tab-active="company">
    <template #title>
      <div class="flex flex-row justify-between items-center w-full">
        <div class="flex flex-row gap-1 items-center">
          <svg-icon icon="company" size="2rem" />
          {{ t("common.labels.edit", { concat: t("common.labels.company") }) }}
        </div>

        <button type="button" class="btn btn-back btn-sm" @click="back()">
          <svg-icon icon="close" size="1.25rem" />
        </button>
      </div>
    </template>

    <template #content>
      <icon-menu
        :tabs="menuTabs"
        :active="selectedMenu"
        @selected="selectedMenu = $event"
      >
        <template #content>
          <template v-if="selectedMenu === 'home'">
            <div class="title">
              {{ t("common.labels.company") }}
            </div>
            <company-form
              width="100%"
              :data="companyPayload"
              :show-avatar="true"
              :avatar="companyData?.avatar?.small"
              @update-data="debounceForm"
              @avatar-add="changeAvatar"
              @avatar-del="delAvatar()"
            />
          </template>

          <template v-if="selectedMenu === 'address'">
            <div class="title">
              {{ t("common.labels.address") }}
            </div>

            <base-table
              :header="addressHeader"
              :data="addressData"
              :paginated="false"
              :show-search="false"
              :options="true"
              :edit="true"
              :del="true"
              @btn:add="addAddress()"
              @btn:edit="updateAddress"
              @btn:del="removeAddress"
            >
              <template #address="{ data }">
                {{ data.city }} - {{ data.state }}
              </template>
            </base-table>
          </template>

          <template v-if="selectedMenu === 'contact'">
            <div class="title">
              {{ t("common.labels.contact") }}
            </div>

            <base-table
              :header="contactHeader"
              :data="contactData"
              :paginated="false"
              :show-search="false"
              :options="true"
              :edit="true"
              :del="true"
              @btn:add="addContact()"
              @btn:edit="updatedContact"
              @btn:del="removeContact"
            >
              <template #type="{ data }">
                {{ t(`common.labels.${data.type}`) }}
              </template>
            </base-table>
          </template>

          <template v-if="selectedMenu === 'employee'">
            <div class="title">
              {{ t("common.labels.employees") }}
            </div>

            <div class="employess">
              <base-table
                :header="employeeHeader"
                :data="employeeData"
                :page="employeePage"
                :pages="employeePages"
                :total="employeeTotal"
                :searched="employeeSearched"
                :limit="employeeLimit"
                :paginated="true"
                :show-search="true"
                :options="true"
                :edit="true"
                :del="true"
                :add="true"
                :upload="true"
                @btn:add="addEmployee()"
                @btn:edit="editEmployee"
                @btn:del="removeEmployee"
                @btn:upload="uploadEmployee()"
                @change:page="changePageEmployee"
                @searching="searchEmployee"
              >
                <template #status="{ data }">
                  <div class="uppercase">
                    {{ t(`common.labels.${data.status.toLowerCase()}`) }}
                  </div>
                </template>
              </base-table>
            </div>
          </template>
        </template>
      </icon-menu>
    </template>
  </register-theme>

  <base-modal
    :show="showModalAddress"
    :title="modalAddressTitle"
    :show-button-confirm="true"
    :button-confirm="
      addressEdit ? t('common.labels.save') : t('common.labels.add')
    "
    :button-close="t('common.labels.cancel')"
    @close="addressCancel()"
    @confirm="submitAddress()"
  >
    <template #content>
      <address-form
        :data="addressPayload"
        @update-data="addressPayload = $event"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showDelAddress"
    :title="t('common.labels.remove', { concat: t('common.labels.company') })"
    :message="
      t('common.texts.remove_message', { concat: selectedAddress?.name })
    "
    @cancel="removeAddressCancel"
    @confirm="removeAddressConfirm()"
  />

  <base-modal
    :show="showModalContact"
    :title="modalContactTitle"
    :show-button-confirm="true"
    :button-confirm="
      contactEdit ? t('common.labels.save') : t('common.labels.add')
    "
    :button-close="t('common.labels.cancel')"
    @close="contactCancel()"
    @confirm="submitContact()"
  >
    <template #content>
      <contact-form
        :data="contactPayload"
        @update-data="contactPayload = $event"
      />
    </template>
  </base-modal>

  <base-modal
    :show="showModalEmployee"
    :title="modalEmployeeTitle"
    :show-button-confirm="true"
    :button-confirm="
      employeeEdit ? t('common.labels.save') : t('common.labels.add')
    "
    :button-close="t('common.labels.cancel')"
    @close="addEmployeeCancel()"
    @confirm="submitEmployee()"
  >
    <template #content>
      <employee-form
        :data="employeePayload"
        @update-data="employeePayload = $event"
      />
    </template>
  </base-modal>

  <base-modal
    :show="showModalFileEmployee"
    :title="
      t('common.labels.upload', {
        concat: t('common.labels.employees'),
      }).toUpperCase()
    "
    :show-button-confirm="true"
    :button-confirm="t('common.labels.send')"
    :button-close="t('common.labels.cancel')"
    @close="uploadEmployeeCancel()"
    @confirm="uploadEmployeeConfirm()"
  >
    <template #content>
      <file-upload-form
        :extensions="fileExtensions"
        :model="true"
        @update-data="fileEmployee = $event"
        @model-download="downloadEmployeeModel()"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showDelContact"
    :title="t('common.labels.remove', { concat: t('common.labels.contact') })"
    :message="
      t('common.texts.remove_message', { concat: selectedContact?.name })
    "
    @cancel="removeContactCancel"
    @confirm="removeContactConfirm()"
  />

  <base-confirm
    :show="showDelEmployee"
    :title="t('common.labels.remove', { concat: t('common.labels.employee') })"
    :message="
      t('common.texts.remove_message', { concat: selectedEmployee?.name })
    "
    @cancel="removeEmployeeCancel"
    @confirm="removeEmployeeConfirm()"
  />

  <base-confirm
    :show="showDelAvatar"
    :title="t('common.labels.remove', { concat: t('common.labels.avatar') })"
    :message="
      t('common.texts.remove_message', { concat: t('common.labels.avatar') })
    "
    @cancel="showDelAvatar = false"
    @confirm="delAvatarConfirm()"
  />
</template>

<style lang="scss" scoped>
.title {
  display: flex;
  flex-direction: column;
  padding-inline: 0.5rem;
  border-bottom: 1px solid var(--border);
  margin-block: 2rem;
  padding-block-end: 0.5rem;
  font-size: 1.15rem;
  font-weight: bold;
  text-transform: uppercase;
}

.employess {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  .header {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 1.5rem;
  }
}
</style>
